import React, { useEffect, useCallback, useState, useRef, useMemo, Suspense, lazy } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useWebSocket, useUser } from '../WebSocketContext';
import ReactDOM from 'react-dom';
import WidgetDashboard from '../WidgetDashboard/WidgetDashboard';
import EventList from '../EventList/EventList';
import Settings from '../Settings';
import Header from './DashboardHeader'; // Import the new Header component
import { Box, Grid, Alert, IconButton, Card, Dialog, DialogTitle, DialogContent, Snackbar, Button, Typography, Fab, Drawer, Badge, Fade, Popover, List, ListItemButton, ListItemText, Divider, Stack } from '@mui/material'; // Added Dialog components
import EventIcon from '@mui/icons-material/Event';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme, styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';

import { DashboardProvider, useDashboard } from './DashboardContext'; // Import the context
import { useTranslation } from 'react-i18next'; // Import useTranslation
import Loading from '../Loading'; // Import the Loading component
import SettingDatasources from '../SettingDatasources';
import AddToCollectionDialog from '../AddToCollectionDialog';
import CloseButton from '../Common/CloseButton';
import ProjectShareDialog from '../Common/ProjectShareDialog';
import WidgetPreviewStep from '../WidgetPreviewStep';
import { useSnackbar } from '../../Utils/SnackBarContext';
import { fetchCollections, fetchProjectCollections } from '../../Utils/collectionsApi';
import {
  normalizePermissionScope,
  scopeHasAnyEntry,
  setProjectScopedAccessFlags
} from '../../Utils/permissionScope';

// Lazy load AddMediaOverlay for better initial page load
const AddMediaOverlay = lazy(() => import('../AddMediaOverlay'));
const TimelineEventsOptimized = lazy(() => import('../Timeline/TimelineEventsOptimized'));
const ActionsLogsDialog = lazy(() => import('../ActionsLogsDialog'));
const normalizePublicAccessLevel = (value) => {
  const normalized = String(value || 'view').trim().toLowerCase();
  return ['view', 'upload', 'view_upload'].includes(normalized) ? normalized : 'view';
};

export const getDashboardErrorTimeeventIds = (details) => {
  if (!details || typeof details !== 'object') {
    return [];
  }

  const rawIds = Array.isArray(details.timeevent_ids)
    ? details.timeevent_ids
    : details.timeevent_id !== undefined && details.timeevent_id !== null
      ? [details.timeevent_id]
      : [];

  return rawIds.reduce((acc, value) => {
    const parsedValue = Number.parseInt(value, 10);
    if (Number.isInteger(parsedValue) && parsedValue > 0) {
      acc.push(parsedValue);
    }
    return acc;
  }, []);
};

export const getDashboardCanUpload = ({ isPublicRouteAccess = false, user = {}, caseCanUpload = false } = {}) => {
  if (isPublicRouteAccess) {
    return Boolean(user?.canUpload);
  }
  return Boolean(caseCanUpload || user?.isAdmin || user?.isEdit);
};

// Styled Badge for the ripple effect
const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#ff1744',
    color: '#ff1744',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

const DashboardContent = () => {
  const { caseid } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { ws, subscribeToMessages, sendMessage } = useWebSocket();
  const user = useUser(); // Get user info with computed properties
  const { title, setTitle, description, setDescription, tags, setTags, deleteTimeEvent, setFilteredTimeEvents } = useDashboard(); // Use context
  const [folder, setFolder] = useState("");
  const [templateInfo, setTemplateInfo] = useState({ id: null, title: '' });
  const [visibleSection, setVisibleSection] = useState("loading");
  const [status, setStatus] = useState("loading");
  const [dashboardOutdated, setDashboardOutdated] = useState(false);
  const [outlookFetchError, setOutlookFetchError] = useState(false);
  const [outlookTokenError, setOutlookTokenError] = useState(false);
  const [outlookApiReAuth, setOutlookApiReAuth] = useState(false);
  const [outlookApiAuthSuccess, setOutlookApiAuthSuccess] = useState(false);
  const [outlookApiAuthFailed, setOutlookApiAuthFailed] = useState(false);
  const [aiTextModelTokenInvalid, setAiTextModelTokenInvalid] = useState(false);
  const [aiTextModelTokenErrorDetails, setAiTextModelTokenErrorDetails] = useState('');
  const [postprocessingError, setPostprocessingError] = useState(false); // Add state for post-processing errors
  const [postprocessingErrorDetails, setPostprocessingErrorDetails] = useState(null); // Add state for error details
  const visibleSectionRef = useRef(visibleSection);
  const [datasourceCount, setDatasourceCount] = useState(0); // Add state for datasource count
  const [hasEvents, setHasEvents] = useState(false); // Track if case has any events
  const [caseAccessMode, setCaseAccessMode] = useState('read');
  const [showDashboardOverride, setShowDashboardOverride] = useState(false); // Allow viewing dashboard without events
  const [caseNotFound, setCaseNotFound] = useState(false); // Track missing case
  const [showCalendarWidget, setShowCalendarWidget] = useState(true);
  const [showTrendWidget, setShowTrendWidget] = useState(true);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [eventListDialogOpen, setEventListDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' }); // Snackbar state
  const [collections, setCollections] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [addToCollectionDialogOpen, setAddToCollectionDialogOpen] = useState(false);
  const [projectCollections, setProjectCollections] = useState([]);
  const [collectionActionLoading, setCollectionActionLoading] = useState(false);
  const [projectShareDialogOpen, setProjectShareDialogOpen] = useState(false);
  const [shareUsers, setShareUsers] = useState([]);
  const [shareUsersLoading, setShareUsersLoading] = useState(false);
  const [shareActionLoadingUsername, setShareActionLoadingUsername] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [isPublicLoading, setIsPublicLoading] = useState(false);
  const [publicAccessLevel, setPublicAccessLevel] = useState('view');
  const [caseCanUpload, setCaseCanUpload] = useState(false);
  const { t } = useTranslation(); // Use translation
  const { openSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [eventToOpen, setEventToOpen] = useState(null);
  const showNewEventsBubble = dashboardOutdated && visibleSection === "timeline" && !hasEvents;



  // States for auto-opening modals after OAuth redirect
  const [autoOpenDatasource, setAutoOpenDatasource] = useState(null);
  const [autoOpenActions, setAutoOpenActions] = useState(null);
  const [autoOpenExportId, setAutoOpenExportId] = useState(null);
  const [autoOpenWidgetTag, setAutoOpenWidgetTag] = useState(null);
  const reviewWidgets = searchParams.get('reviewWidgets') === 'true';
  const headerOffset = reviewWidgets ? 0 : 56;
  const [dashboardErrorsAnchorEl, setDashboardErrorsAnchorEl] = useState(null);
  const [dashboardErrorLogsDialog, setDashboardErrorLogsDialog] = useState({
    open: false,
    title: '',
    caseid: null,
    timeeventId: null
  });

  // Check if user has read-only permissions using the user object
  const isReadOnly = user.isReadOnly;
  const isPublicRouteAccess = Boolean(user.isPublicAccess);
  const canUpload = getDashboardCanUpload({ isPublicRouteAccess, user, caseCanUpload });
  const canManageProject = Boolean(user.canManageProject);
  const isUploadOnlyAccess = caseAccessMode === 'upload_only';

  const isProjectReadDeniedPayload = useCallback((payload) => {
    if (!payload || typeof payload !== 'object') {
      return false;
    }
    const rawMessage = payload.message || payload.error || payload.error_message || '';
    const normalizedMessage = String(rawMessage).toLowerCase();
    return normalizedMessage.includes('do not have read access to this project')
      || (normalizedMessage.includes('access denied') && normalizedMessage.includes('read access'));
  }, []);

  // No datasource dialog — we instead show datasources in the main timeline area when there are no events.
  // Keep handling autoOpenDatasource and autoOpenActions via state as before.
  // Handle OAuth redirect parameters to reopen modals
  useEffect(() => {
    const openDatasource = searchParams.get('openDatasource');
    const connector = searchParams.get('connector');
    const openActions = searchParams.get('openActions');
    const exportId = searchParams.get('exportId');
    const openWidgetSettings = searchParams.get('openWidgetSettings');
    const widgetTag = searchParams.get('widgetTag');

    let shouldUpdateParams = false;
    const nextParams = new URLSearchParams(searchParams);

    if (openDatasource === 'true' && connector) {
      setAutoOpenDatasource(connector);
      nextParams.delete('openDatasource');
      nextParams.delete('connector');
      shouldUpdateParams = true;
    }

    if (openActions === 'true') {
      setAutoOpenActions(true);
      if (exportId) {
        setAutoOpenExportId(exportId);
      }
      nextParams.delete('openActions');
      nextParams.delete('exportId');
      shouldUpdateParams = true;
    }

    if (openWidgetSettings === 'true') {
      if (widgetTag) {
        setAutoOpenWidgetTag(widgetTag);
      }
      setVisibleSection("timeline");
      nextParams.delete('openWidgetSettings');
      nextParams.delete('widgetTag');
      shouldUpdateParams = true;
    }

    if (shouldUpdateParams) {
      setSearchParams(nextParams);
    }
  }, [searchParams, setSearchParams]);

  const handleWidgetPreviewDone = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('reviewWidgets');
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  const handleOpenCaseSettings = useCallback(() => {
    setSettingsDialogOpen(true);
  }, []);

  const handleCloseCaseSettings = useCallback(() => {
    setSettingsDialogOpen(false);
  }, []);

  const handleOpenEventList = useCallback(() => {
    setEventListDialogOpen(true);
  }, []);

  const handleOpenDashboardErrors = useCallback((event) => {
    setDashboardErrorsAnchorEl(event.currentTarget);
  }, []);

  const handleCloseDashboardErrors = useCallback(() => {
    setDashboardErrorsAnchorEl(null);
  }, []);

  const handleOpenDashboardErrorLogs = useCallback((item) => {
    if (!item) {
      return;
    }

    setDashboardErrorLogsDialog({
      open: true,
      title: `${t('dashboard.error_logs_dialog_title', 'Error logs')} - ${item.title || t('dashboard.errors_popover_title', 'Dashboard errors')}`,
      caseid,
      timeeventId: item.logsTimeeventId ?? null
    });
    handleCloseDashboardErrors();
  }, [caseid, handleCloseDashboardErrors, t]);

  const handleCloseDashboardErrorLogs = useCallback(() => {
    setDashboardErrorLogsDialog({
      open: false,
      title: '',
      caseid: null,
      timeeventId: null
    });
  }, []);

  const handleOpenDashboardErrorEvent = useCallback((eventId) => {
    const parsedEventId = Number.parseInt(eventId, 10);
    if (!Number.isInteger(parsedEventId) || parsedEventId <= 0) {
      return;
    }

    handleCloseDashboardErrors();
    setEventToOpen(parsedEventId);
    setDrawerOpen(true);
    setVisibleSection('timeline');
  }, [handleCloseDashboardErrors]);

  const handleOpenDashboardErrorAction = useCallback((actionId) => {
    const normalizedActionId = String(actionId || '').trim();
    if (!normalizedActionId) {
      return;
    }

    handleCloseDashboardErrors();
    setAutoOpenActions(true);
    setAutoOpenExportId(normalizedActionId);
  }, [handleCloseDashboardErrors]);

  const handleOpenDashboardErrorSettings = useCallback((mode) => {
    handleCloseDashboardErrors();
    if (mode === 'root') {
      navigate('/Settings');
      return;
    }

    handleOpenCaseSettings();
  }, [handleCloseDashboardErrors, handleOpenCaseSettings, navigate]);

  const handleOpenUploadMenu = useCallback(() => {
    if (!canUpload || typeof window === 'undefined') {
      return;
    }
    window.dispatchEvent(new CustomEvent('dashboard:open-add-menu'));
  }, [canUpload]);

  const handleCloseEventList = useCallback(() => {
    setEventListDialogOpen(false);
  }, []);

  const dashboardErrorItems = useMemo(() => {
    const items = [];

    if (outlookTokenError) {
      items.push({
        id: 'outlookTokenError',
        title: t('dashboard.error_fetching_outlook_emails_bad_credential'),
        message: t('dashboard.error_fetching_outlook_emails_bad_credential'),
        logsTitle: t('dashboard.error_logs_dialog_title', 'Error logs'),
        logsTimeeventId: null,
        eventIds: [],
        actionId: '',
        actionLabel: '',
        settingsMode: 'case'
      });
    }

    if (outlookFetchError) {
      items.push({
        id: 'outlookFetchError',
        title: t('dashboard.error_fetching_outlook_emails'),
        message: t('dashboard.error_fetching_outlook_emails'),
        logsTitle: t('dashboard.error_logs_dialog_title', 'Error logs'),
        logsTimeeventId: null,
        eventIds: [],
        actionId: '',
        actionLabel: '',
        settingsMode: 'case'
      });
    }

    if (outlookApiReAuth) {
      items.push({
        id: 'outlookApiReAuth',
        title: t('dashboard.reverify_outlook_account'),
        message: t('dashboard.reverify_outlook_account'),
        logsTitle: t('dashboard.error_logs_dialog_title', 'Error logs'),
        logsTimeeventId: null,
        eventIds: [],
        actionId: '',
        actionLabel: '',
        settingsMode: 'case'
      });
    }

    if (outlookApiAuthFailed) {
      items.push({
        id: 'outlookApiAuthFailed',
        title: t('dashboard.verify_outlook_account_failed'),
        message: t('dashboard.verify_outlook_account_failed'),
        logsTitle: t('dashboard.error_logs_dialog_title', 'Error logs'),
        logsTimeeventId: null,
        eventIds: [],
        actionId: '',
        actionLabel: '',
        settingsMode: 'case'
      });
    }

    if (aiTextModelTokenInvalid) {
      const aiTokenMessage = aiTextModelTokenErrorDetails
        ? `${t('dashboard.invalid_ai_text_model_token')}\n${aiTextModelTokenErrorDetails}`
        : t('dashboard.invalid_ai_text_model_token');

      items.push({
        id: 'aiTextModelTokenInvalid',
        title: t('dashboard.invalid_ai_text_model_token'),
        message: aiTokenMessage,
        logsTitle: t('dashboard.error_logs_dialog_title', 'Error logs'),
        logsTimeeventId: null,
        eventIds: [],
        actionId: '',
        actionLabel: '',
        settingsMode: 'root'
      });
    }

    if (postprocessingError && postprocessingErrorDetails) {
      const postprocessingTimeeventIds = getDashboardErrorTimeeventIds(postprocessingErrorDetails);
      const actionId = String(postprocessingErrorDetails.dataexport_id ?? '').trim();
      const actionLabel = String(postprocessingErrorDetails.dataexport_name || '').trim();
      const errorMessage = String(postprocessingErrorDetails.error_message || '').trim()
        || t('dashboard.postprocessing_error_default', 'An action failed during post-processing.');

      items.push({
        id: 'postprocessingError',
        title: t('dashboard.postprocessing_error_title', 'Post-processing error'),
        message: [
          actionLabel ? t('dashboard.postprocessing_error_action', 'Action: {{name}}', { name: actionLabel }) : '',
          actionId ? t('dashboard.postprocessing_error_action_id', 'Action ID: {{id}}', { id: actionId }) : '',
          errorMessage
        ].filter(Boolean).join('\n'),
        logsTitle: t('dashboard.error_logs_dialog_title', 'Error logs'),
        logsTimeeventId: postprocessingTimeeventIds[0] ?? null,
        eventIds: postprocessingTimeeventIds,
        actionId,
        actionLabel,
        settingsMode: '',
        onDismiss: () => {
          setPostprocessingError(false);
          setPostprocessingErrorDetails(null);
        }
      });
    }

    return items;
  }, [
    aiTextModelTokenErrorDetails,
    aiTextModelTokenInvalid,
    outlookApiAuthFailed,
    outlookApiReAuth,
    outlookFetchError,
    outlookTokenError,
    postprocessingError,
    postprocessingErrorDetails,
    t
  ]);

  const closeAddToCollectionDialog = useCallback(() => {
    setAddToCollectionDialogOpen(false);
    setCollectionsLoading(false);
    setCollectionActionLoading(false);
  }, []);

  const openAddToCollectionDialog = useCallback(() => {
    setCollectionsLoading(true);
    setAddToCollectionDialogOpen(true);
    fetchCollections()
      .then((payload) => {
        setCollections(Array.isArray(payload.collections) ? payload.collections : []);
      })
      .catch((error) => {
        console.error('Error loading collections:', error);
        setCollections([]);
      })
      .finally(() => {
        setCollectionsLoading(false);
      });
  }, []);

  const requestProjectShareUsers = useCallback(() => {
    if (!sendMessage) {
      openSnackbar(t('project_share.connection_error', 'Connection error. Please try again.'), 3000, 'error');
      return false;
    }
    setShareUsersLoading(true);
    sendMessage({ command: 'get_all_users' });
    return true;
  }, [sendMessage, openSnackbar, t]);

  const openProjectShareDialog = useCallback(() => {
    if (!requestProjectShareUsers()) {
      return;
    }
    setProjectShareDialogOpen(true);
  }, [requestProjectShareUsers]);

  const closeProjectShareDialog = useCallback(() => {
    if (shareActionLoadingUsername) {
      return;
    }
    setProjectShareDialogOpen(false);
  }, [shareActionLoadingUsername]);

  const handleApplyProjectAccess = useCallback((userRow, accessFlags) => {
    const username = String(userRow?.username || '').trim();
    const normalizedProjectId = Number(caseid);
    if (!username || !Number.isInteger(normalizedProjectId) || normalizedProjectId <= 0) {
      openSnackbar(t('project_share.invalid_request', 'Unable to update access for this project.'), 3000, 'error');
      return;
    }
    if (!sendMessage) {
      openSnackbar(t('project_share.connection_error', 'Connection error. Please try again.'), 3000, 'error');
      return;
    }

    const currentScope = normalizePermissionScope(userRow?.permission_scope);
    const nextScope = setProjectScopedAccessFlags(currentScope, normalizedProjectId, accessFlags);
    const nextPermission = scopeHasAnyEntry(nextScope) ? 'scoped' : 'view_only';

    setShareActionLoadingUsername(username);
    sendMessage({
      command: 'update_user',
      data: {
        username,
        permission: nextPermission,
        permission_scope: nextPermission === 'scoped' ? nextScope : undefined
      }
    });
  }, [caseid, sendMessage, openSnackbar, t]);

  const handleToggleProjectPublic = useCallback((nextValue, nextAccessLevel = publicAccessLevel) => {
    if (!sendMessage || !caseid) {
      openSnackbar(t('project_share.connection_error', 'Connection error. Please try again.'), 3000, 'error');
      return;
    }
    setIsPublicLoading(true);
    sendMessage({
      command: 'update_case_public',
      caseid,
      isPublic: nextValue,
      publicAccessLevel: normalizePublicAccessLevel(nextAccessLevel)
    });
  }, [sendMessage, caseid, openSnackbar, t, publicAccessLevel]);

  const requestAddToCollection = useCallback((collectionId) => {
    if (!collectionId) {
      return false;
    }
    if (!sendMessage || !caseid) {
      openSnackbar(t('collection.add_error_connection', 'Connection error. Please try again.'), 3000, 'error');
      return false;
    }

    setCollectionActionLoading(true);
    sendMessage({
      command: 'addProjectToCollection',
      project_id: caseid,
      collection_id: collectionId
    });
    return true;
  }, [sendMessage, caseid, openSnackbar, t]);

  const handleAddToCollectionConfirm = useCallback((collectionId) => {
    if (requestAddToCollection(collectionId)) {
      closeAddToCollectionDialog();
    }
  }, [requestAddToCollection, closeAddToCollectionDialog]);

  const openSidebarForCollection = useCallback((collectionId) => {
    if (!collectionId || typeof window === 'undefined') {
      return;
    }
    window.dispatchEvent(new CustomEvent('dashboard:open-collection', { detail: { collectionId } }));
  }, []);

  const assignedCollectionsPreview = useMemo(
    () => (projectCollections || []).slice(0, 3),
    [projectCollections]
  );
  const hasMoreAssignedCollections = projectCollections.length > assignedCollectionsPreview.length;
  const hasAdditionalCollections = collections.length > projectCollections.length;

  // Keep ref in sync with state
  useEffect(() => {
    visibleSectionRef.current = visibleSection;
  }, [visibleSection]);

  useEffect(() => {
    setCollectionsLoading(true);
    fetchCollections()
      .then((payload) => {
        setCollections(Array.isArray(payload.collections) ? payload.collections : []);
      })
      .catch((error) => {
        console.error('Error loading collections:', error);
        setCollections([]);
      })
      .finally(() => {
        setCollectionsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (caseid) {
      fetchProjectCollections(caseid)
        .then((payload) => {
          setProjectCollections(Array.isArray(payload.collections) ? payload.collections : []);
        })
        .catch((error) => {
          console.error('Error loading project collections:', error);
          setProjectCollections([]);
        });
    }
  }, [caseid]);

  const handleWebSocketMessage = useCallback((message) => {
    switch (message.command) {
      case "getcase":
        // If server indicates the case doesn't exist, show a not-found view
        if (!message.data || !message.data.case) {
          console.log('Dashboard - case not found for id:', caseid);
        setCaseNotFound(true);
        setStatus('notfound');
          // clear any previous header content
          setTitle('');
          setDescription('');
          setFolder('');
          setTemplateInfo({ id: null, title: '' });
          setTags([]);
          setCaseAccessMode('read');
          setCaseCanUpload(false);
          return;
        }

        const creationWizardState = message.data.creationWizardState || {};
        if (!isPublicRouteAccess && creationWizardState.isIncomplete) {
          navigate(`/add?resumeCaseId=${encodeURIComponent(caseid)}`, { replace: true });
          return;
        }

        setCaseNotFound(false);
        const casePayload = message.data.case || {};
        const accessMode = message.data.access_mode ?? casePayload.access_mode;
        const canReadCase = message.data.can_read ?? casePayload.can_read;
        const canUploadCase = message.data.can_upload ?? casePayload.can_upload;
        const uploadOnlyFromMode = accessMode === 'upload_only';
        const uploadOnlyFromFlags = canReadCase === false && canUploadCase === true;
        setCaseAccessMode(uploadOnlyFromMode || uploadOnlyFromFlags ? 'upload_only' : 'read');
        setTitle(casePayload.title);
        setDescription(casePayload.description);
        setFolder(casePayload.folderlocation);
        setTemplateInfo({
          id: casePayload.templateId ?? null,
          title: casePayload.templateTitle || ''
        });
        setTags(casePayload.customFields);
        const rawShowCalendar = casePayload.showCalendarWidget;
        const rawShowTrend = casePayload.showTrendWidget;
        setShowCalendarWidget(rawShowCalendar === undefined || rawShowCalendar === null ? true : !!rawShowCalendar);
        setShowTrendWidget(rawShowTrend === undefined || rawShowTrend === null ? true : !!rawShowTrend);
        setIsPublic(!!casePayload.isPublic);
        setPublicAccessLevel(normalizePublicAccessLevel(casePayload.publicAccessLevel));
        setCaseCanUpload(!!canUploadCase);
        if (reviewWidgets && casePayload.widgetGenerationStatus === 'ready') {
          handleWidgetPreviewDone();
        }

        var datasourceCount = message.data.datasource_count;
        var hasEvents = message.data.has_events || false;
        console.log('Dashboard - datasourceCount from server:', datasourceCount);
        console.log('Dashboard - hasEvents from server:', hasEvents);
        console.log('Dashboard - case data:', casePayload);
        setDatasourceCount(datasourceCount); // Set the datasource count
        setHasEvents(hasEvents); // Set whether case has events
        if (visibleSectionRef.current === "loading") {
          setVisibleSection("timeline"); // Fallback to timeline if still in loading state
        }
        setStatus("loaded");
        break;
      case "getOtherShowCalendarWidget": {
        const payload = message.data || {};
        if (payload.status === 'success') {
          const value = payload.show_calendar_widget;
          setShowCalendarWidget(value === undefined || value === null ? true : !!value);
        }
        break;
      }
      case "updateOtherShowCalendarWidget": {
        const payload = message.data || {};
        if (payload.status === 'success') {
          const value = payload.show_calendar_widget;
          setShowCalendarWidget(value === undefined || value === null ? true : !!value);
        }
        break;
      }
      case "getOtherShowTrendWidget": {
        const payload = message.data || {};
        if (payload.status === 'success') {
          const value = payload.show_trend_widget;
          setShowTrendWidget(value === undefined || value === null ? true : !!value);
        }
        break;
      }
      case "updateOtherShowTrendWidget": {
        const payload = message.data || {};
        if (payload.status === 'success') {
          const value = payload.show_trend_widget;
          setShowTrendWidget(value === undefined || value === null ? true : !!value);
        }
        break;
      }
      case "update_case_public_response": {
        setIsPublicLoading(false);
        if (message.data?.success) {
          setIsPublic(!!message.data.isPublic);
          setPublicAccessLevel(normalizePublicAccessLevel(message.data.publicAccessLevel));
          openSnackbar(
            message.data.isPublic
              ? t('settings.others.public_link_info', 'This project is currently public.')
              : t('project_share.private_link_info', 'This project is now private.'),
            3000,
            'success'
          );
        } else {
          openSnackbar(message.data?.error || t('project_share.update_public_error', 'Failed to update public access'), 3000, 'error');
        }
        break;
      }
      case "get_all_users": {
        if (message.data?.status === 'success') {
          setShareUsers(message.data.users || []);
        } else {
          setShareUsers([]);
          openSnackbar(message.data?.message || t('project_share.load_users_error', 'Failed to load users'), 3000, 'error');
        }
        setShareUsersLoading(false);
        break;
      }
      case "update_user": {
        if (shareActionLoadingUsername) {
          if (message.data?.status === 'success') {
            openSnackbar(message.data?.message || t('project_share.update_success', 'Project access updated successfully'), 3000, 'success');
            if (sendMessage) {
              setShareUsersLoading(true);
              sendMessage({ command: 'get_all_users' });
            }
          } else {
            openSnackbar(message.data?.message || t('project_share.update_error', 'Failed to update project access'), 3000, 'error');
          }
          setShareActionLoadingUsername('');
          setShareUsersLoading(false);
          break;
        }
        break;
      }
      case "addProjectToCollection": {
        setCollectionActionLoading(false);
        if (message.data && message.data.status === 'success') {
          openSnackbar(message.data.message || t('collection.case_added_success', 'Case added to collection successfully'), 3000, 'success');
        } else {
          const errorMsg = message.data?.message || t('collection.add_error', 'Failed to add project to collection');
          openSnackbar(errorMsg, 3000, 'error');
        }
        if (caseid) {
          fetchProjectCollections(caseid)
            .then((payload) => {
              setProjectCollections(Array.isArray(payload.collections) ? payload.collections : []);
            })
            .catch((error) => {
              console.error('Error refreshing project collections:', error);
              setProjectCollections([]);
            });
        }
        break;
      }
      case "deleteTimeEvent":
        // Handle time event deletion
        if (message.data.success) {
          // Remove the deleted time event from the state using the context function
          deleteTimeEvent(message.data.id);
          // Also remove from filtered time events if they exist
          setFilteredTimeEvents(prevFiltered =>
            prevFiltered.filter(event => parseInt(event.id) !== parseInt(message.data.id))
          );
          // Show success snackbar
          setSnackbar({
            open: true,
            message: t('timeevent.delete_success') || 'Time event deleted successfully',
            severity: 'success'
          });
        }
        break;

      case "notifications":
        console.log("Received notification:", message.data);
        console.log("Message ID:", message.data.id, "Case ID:", caseid);
        console.log("Message Type:", message.data.type);

        if (parseInt(message.data.id) !== parseInt(caseid)) {
          console.log("Message ID does not match case ID, skipping...");
          return;
        }

        if (message.data.type === "file_changed_completed") {
          console.log("Timeline updated found");
          setDashboardOutdated(true);
        } else if (message.data.type === "invalid_outlook_token") {
          console.log("invalid outlook token");
          setOutlookTokenError(true);
        } else if (message.data.type === "error_fetching_emails_from_outlook_api") {
          console.log("error fetching emails from outlook api");
          setOutlookFetchError(true);
        } else if (message.data.type === "reauthenticate_outlook_api") {
          console.log("reauthenticate outlook api");
          setOutlookApiReAuth(true);
        } else if (message.data.type === "outlook_api_auth_success") {
          console.log("outlook api auth success");
          setOutlookApiAuthSuccess(true);
        } else if (message.data.type === "outlook_api_auth_failed") {
          console.log("outlook api auth failed");
          setOutlookApiAuthFailed(true);
        } else if (message.data.type === "invalid_ai_text_model_token") {
          console.log("invalid ai text model token");
          setAiTextModelTokenErrorDetails(
            String(
              message.data.error_message
              || message.data.error
              || message.data.status_message
              || ''
            ).trim()
          );
          setAiTextModelTokenInvalid(true);
        } else if (message.data.type === "postprocessing_error") {
          console.log("postprocessing error", message.data);
          const timeeventIds = Array.isArray(message.data.timeevent_ids)
            ? message.data.timeevent_ids.filter((value) => value !== null && value !== undefined)
            : message.data.timeevent_id !== undefined && message.data.timeevent_id !== null
              ? [message.data.timeevent_id]
              : [];
          setPostprocessingError(true);
          setPostprocessingErrorDetails({
            dataexport_id: message.data.dataexport_id,
            dataexport_name: message.data.dataexport_name,
            timeevent_ids: timeeventIds,
            error_message: message.data.error_message,
            timestamp: message.data.timestamp
          });
        }
        break;
      case "getWidgets":
      case "getDashboardSnapshots": {
        const payload = message.data || {};
        const payloadCaseId = payload.caseid ?? payload.case_id ?? payload.project_id ?? payload.id;
        const isCurrentCase = !payloadCaseId || String(payloadCaseId) === String(caseid);
        const uploadOnlyFromMode = payload.access_mode === 'upload_only';
        const uploadOnlyFromFlags = payload.can_read === false && payload.can_upload === true;
        const uploadOnlyFromReadDenied = payload.status === 'error' && isProjectReadDeniedPayload(payload);

        if (isCurrentCase && canUpload && (uploadOnlyFromMode || uploadOnlyFromFlags || uploadOnlyFromReadDenied)) {
          setCaseAccessMode('upload_only');
        }
        break;
      }
      default:
        break;
    }
  }, [caseid, canUpload, deleteTimeEvent, handleWidgetPreviewDone, isProjectReadDeniedPayload, isPublicRouteAccess, navigate, reviewWidgets, setDescription, setFilteredTimeEvents, setTags, setTitle, t, sendMessage, openSnackbar, shareActionLoadingUsername]);

  const getCase = useCallback(() => {
    sendMessage({ command: 'getcase', caseid });
  }, [sendMessage, caseid]);

  useEffect(() => {
    if (ws) {
      setTitle("Loading...");
      setDescription("Loading...");
      setStatus("loading");
      setCaseNotFound(false);
      setFolder("Loading...");
      getCase();
      console.log("Getting case");
    }
  }, [ws, caseid, getCase, setTitle, setDescription]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      // Message is already parsed in WebSocketContext
      handleWebSocketMessage(parsedMessage);
    });
    return unsubscribe;
  }, [subscribeToMessages, handleWebSocketMessage]);

  const openFolder = () => {
    sendMessage({ command: 'openfolder', caseid });
  };

  const RenameAll = () => {
    sendMessage({ command: 'renameall', caseid });
  };
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };



  // Show loading spinner when still loading time events
  const renderMainContent = () => {
    if (status === "loading") {
      return (
        <Box sx={{
          width: '100%',
          height: '100%',
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          pt: 1,
          contain: 'layout style', // Improve layout performance
        }}>
          <Card sx={{ boxShadow: 6, width: '100%', margin: "10px", borderRadius: 5, minHeight: '30vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '30vh' }}>
              <Loading />
            </Box>
          </Card>
        </Box>
      );
    }

    // Show datasources setup when no events exist yet — instead of returning the whole page,
    // we now open a dialog with the settings, while still rendering the dashboard behind it.
    console.log('Dashboard render - datasourceCount:', datasourceCount, 'hasEvents:', hasEvents, 'status:', status, 'visibleSection:', visibleSection);

    console.log('Dashboard - Showing main content (WidgetDashboard or EventList)');
    return (
      <Box sx={{
        backgroundColor: '#f7f7ff',
        background: 'linear-gradient(135deg, #f7f7ff 0%, #ffffff 70%, rgba(87, 87, 176, 0.08) 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: headerOffset ? `calc(100vh - ${headerOffset}px)` : '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: reviewWidgets && !hasEvents ? 'center' : 'flex-start',
        overflow: 'hidden', // Prevent horizontal scroll
        contain: 'layout style', // Improve layout performance
      }}>
        {visibleSection === "timeline" && canUpload && (
          <Suspense fallback={null}>
            <AddMediaOverlay caseid={caseid} fullPage canUpload={canUpload} />
          </Suspense>
        )}
        <Box sx={{ padding: 1, width: '100%' }}>
          <Grid container justifyContent="center">
            <Grid item xs={12} md={11.5}>
              {visibleSection === "timeline" && isUploadOnlyAccess && canUpload && (
                <Card sx={{ boxShadow: 6, width: '100%', margin: "10px", borderRadius: 5, minHeight: '40vh' }}>
                  <Box sx={{ p: { xs: 2, sm: 4 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>
                      {t('dashboard.upload_only_title', 'Upload files to this project')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 640, mb: 3 }}>
                      {t('dashboard.upload_only_subtitle', 'You have upload-only access. Use Add to upload files and see your uploaded items in timeline list.')}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={handleOpenUploadMenu}
                      sx={{
                        borderRadius: '999px',
                        px: 2.5,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 700,
                        backgroundColor: '#5757b0',
                        '&:hover': { backgroundColor: '#4a4aa0' }
                      }}
                    >
                      {t('dashboard.upload_only_add_button', 'Add / Upload')}
                    </Button>
                  </Box>
                </Card>
              )}
              {visibleSection === "timeline" && !isUploadOnlyAccess && !hasEvents && !showDashboardOverride && (
                <>
                  <Card sx={{ boxShadow: 6, width: '100%', margin: "10px", borderRadius: 5, minHeight: '50vh' }}>
                    <Box sx={{ p: { xs: 2, sm: 4 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                      <Box sx={{ textAlign: 'center', width: '100%', maxWidth: '900px' }}>
                        {reviewWidgets ? (
                          <WidgetPreviewStep
                            caseid={caseid}
                            onContinue={handleWidgetPreviewDone}
                          />
                        ) : (
                          <>
                          <Box sx={{ mt: 2, mb: 4, textAlign: 'center', width: '100%' }}>
                            <Typography
                              variant="h5"
                              component="h2"
                              sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}
                            >
                              {t('dashboard.connect_data_sources_title', 'Connect your data sources')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 680, mx: 'auto' }}>
                              {t('dashboard.connect_data_sources_subtitle', 'Connect email, cloud drives, and integrations to populate this dashboard with documents and events.')}
                            </Typography>
                          </Box>

                            <SettingDatasources
                              caseid={caseid}
                              hideHeader={true}
                              autoOpenConnector={autoOpenDatasource}
                              onConnectorOpened={() => { setAutoOpenDatasource(null); }}
                            />
                            <Box sx={{ mt: 2 }}>
                              <Button
                                variant="text"
                                color="primary"
                                onClick={() => setShowDashboardOverride(true)}
                              >
                                {t('dashboard.continue_to_dashboard', 'Continue to Dashboard without adding data')}
                              </Button>
                            </Box>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Card>
                </>
              )}
              {visibleSection === "timeline" && !isUploadOnlyAccess && (hasEvents || showDashboardOverride) && (
                <>
                  <WidgetDashboard
                    caseid={caseid}
                    folder={folder}
                    templateInfo={templateInfo}
                    showCalendarWidget={showCalendarWidget}
                    showTrendWidget={showTrendWidget}
                    onOpenTimeline={(id) => { setEventToOpen(id); setDrawerOpen(true); }}
                    autoOpenWidgetTag={autoOpenWidgetTag}
                    onAutoOpenWidgetTagHandled={() => setAutoOpenWidgetTag(null)}
                  />
                </>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  };

  return caseNotFound ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
      <Card sx={{ maxWidth: 800, width: '100%', p: 3, borderRadius: 4, boxShadow: 6 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 'bold' }}>{t('dashboard.project_not_found_title')}</Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>{t('dashboard.project_not_found_message')}</Typography>
          <Button variant="contained" onClick={() => navigate('/')}>
            {t('dashboard.back_to_projects')}
          </Button>
        </Box>
      </Card>
    </Box>
  ) : (
    <>
      {!reviewWidgets && (
        <Header
          title={title}
          setTitle={setTitle}
          description={description}
          setDescription={setDescription}
          folder={folder}
          RenameAll={RenameAll}
          openFolder={openFolder}
          ws={ws}
          caseid={caseid}
          tags={tags}
          templateInfo={templateInfo}
          visibleSection={visibleSection}
          isReadOnly={isReadOnly}
          canManageProject={canManageProject}
          autoOpenDatasource={autoOpenDatasource}
          onDatasourceOpened={() => setAutoOpenDatasource(null)}
          autoOpenActions={autoOpenActions}
          autoOpenExportId={autoOpenExportId}
          onActionsOpened={() => {
            setAutoOpenActions(null);
            setAutoOpenExportId(null);
          }}
          onSettingsOpen={handleOpenCaseSettings}
          assignedCollections={assignedCollectionsPreview}
          hasMoreAssignedCollections={hasMoreAssignedCollections}
          hasAdditionalCollections={hasAdditionalCollections}
          collectionsLoading={collectionsLoading}
          collectionActionLoading={collectionActionLoading}
          onMoreCollections={openAddToCollectionDialog}
          onCollectionChipClick={openSidebarForCollection}
          settingsDialogOpen={settingsDialogOpen}
          isAdmin={user.isAdmin}
          onShareOpen={openProjectShareDialog}
        />
      )}

      <ProjectShareDialog
        open={projectShareDialogOpen}
        onClose={closeProjectShareDialog}
        users={shareUsers}
        currentUsername={user.username}
        projectId={caseid}
        projectTitle={title}
        onApplyAccess={handleApplyProjectAccess}
        savingUsername={shareActionLoadingUsername}
        loading={shareUsersLoading}
        isPublic={isPublic}
        isPublicLoading={isPublicLoading}
        publicAccessLevel={publicAccessLevel}
        onTogglePublic={handleToggleProjectPublic}
      />

      {/* Aggregate dashboard errors into one floating entry so the page stays compact. */}
      {dashboardErrorItems.length > 0 && ReactDOM.createPortal(
        <Box sx={{
          position: 'fixed',
          top: 20,
          left: 'calc(20px + var(--statusbar-badge-row-width, 0px))',
          zIndex: 1200
        }}>
          <Tooltip
            title={t('dashboard.errors_button_title', 'Dashboard errors')}
            placement="bottom-start"
            arrow
            sx={{
              '& .MuiTooltip-tooltip': {
                backgroundColor: 'rgba(211, 47, 47, 0.96)',
                color: 'white',
                fontSize: '0.75rem',
                borderRadius: '8px',
                maxWidth: '280px'
              },
              '& .MuiTooltip-arrow': {
                color: 'rgba(211, 47, 47, 0.96)'
              }
            }}
          >
            <Box sx={{ cursor: 'pointer' }}>
              <Badge
                badgeContent={dashboardErrorItems.length}
                max={99}
                overlap="circular"
                invisible={dashboardErrorItems.length <= 0}
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: '#d32f2f',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    minWidth: '18px',
                    height: '18px',
                    padding: '0 4px',
                    border: '2px solid rgba(255, 255, 255, 0.96)',
                    boxShadow: '0 2px 6px rgba(211, 47, 47, 0.28)'
                  }
                }}
              >
                <IconButton
                  size="small"
                  onClick={handleOpenDashboardErrors}
                  aria-haspopup="menu"
                  aria-controls={Boolean(dashboardErrorsAnchorEl) ? 'dashboard-errors-popover' : undefined}
                  aria-expanded={Boolean(dashboardErrorsAnchorEl) ? 'true' : undefined}
                  aria-label={t('dashboard.errors_button_aria', 'Open dashboard errors')}
                  sx={{
                    width: 34,
                    height: 34,
                    color: '#d32f2f',
                    backgroundColor: 'rgba(255, 255, 255, 0.96)',
                    border: '1px solid rgba(211, 47, 47, 0.24)',
                    boxShadow: '0 4px 16px rgba(211, 47, 47, 0.18)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.25s ease-in-out',
                    '&:hover': {
                      backgroundColor: '#fff5f5',
                      boxShadow: '0 6px 20px rgba(211, 47, 47, 0.24)',
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  <ErrorOutlineIcon fontSize="small" />
                </IconButton>
              </Badge>
            </Box>
          </Tooltip>
        </Box>,
        document.body
      )}

      <Popover
        id="dashboard-errors-popover"
        open={Boolean(dashboardErrorsAnchorEl) && dashboardErrorItems.length > 0}
        anchorEl={dashboardErrorsAnchorEl}
        onClose={handleCloseDashboardErrors}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 1,
            width: { xs: 'calc(100vw - 32px)', sm: 420 },
            maxWidth: 420,
            borderRadius: 3,
            border: '1px solid rgba(211, 47, 47, 0.18)',
            boxShadow: '0 16px 38px rgba(20, 24, 74, 0.18)',
            overflow: 'hidden'
          }
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#c62828' }}>
            {t('dashboard.errors_popover_title', 'Dashboard errors')}
          </Typography>
          <Button size="small" onClick={handleCloseDashboardErrors} sx={{ textTransform: 'none', fontWeight: 700 }}>
            {t('dashboard.dismiss', 'Dismiss')}
          </Button>
        </Stack>
        <Divider />
        <List dense disablePadding>
          {dashboardErrorItems.map((item, index) => (
            <React.Fragment key={item.id}>
              <ListItemButton
                component="div"
                onClick={() => handleOpenDashboardErrorLogs(item)}
                sx={{
                  px: 2,
                  py: 1.25,
                  alignItems: 'flex-start',
                  gap: 1.5,
                  '&:hover': {
                    backgroundColor: '#fff5f5'
                  }
                }}
              >
                <ListItemText
                  primary={item.title}
                  secondary={(
                    <Stack spacing={1} sx={{ mt: 0.5 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#6b7280' }}>
                        {item.message}
                      </Typography>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {item.eventIds.map((eventId) => (
                          <Button
                            key={`${item.id}:event:${eventId}`}
                            size="small"
                            variant="outlined"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOpenDashboardErrorEvent(eventId);
                            }}
                            sx={{
                              textTransform: 'none',
                              borderRadius: 999,
                              fontWeight: 700
                            }}
                          >
                            {t('dashboard.error_event_chip', 'Event #{{id}}', { id: eventId })}
                          </Button>
                        ))}
                        {item.actionId && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOpenDashboardErrorAction(item.actionId);
                            }}
                            sx={{
                              textTransform: 'none',
                              borderRadius: 999,
                              fontWeight: 700
                            }}
                          >
                            {item.actionLabel
                              ? t('dashboard.error_action_chip', 'Action: {{name}}', { name: item.actionLabel })
                              : t('dashboard.error_action_chip_fallback', 'Action #{{id}}', { id: item.actionId })}
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenDashboardErrorLogs(item);
                          }}
                          sx={{
                            textTransform: 'none',
                            borderRadius: 999,
                            fontWeight: 700
                          }}
                        >
                          {t('dashboard.error_logs_chip', 'Logs')}
                        </Button>
                        {item.onDismiss && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(event) => {
                              event.stopPropagation();
                              item.onDismiss();
                            }}
                            sx={{
                              textTransform: 'none',
                              borderRadius: 999,
                              fontWeight: 700
                            }}
                          >
                            {t('dashboard.dismiss', 'Dismiss')}
                          </Button>
                        )}
                        {item.settingsMode && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOpenDashboardErrorSettings(item.settingsMode);
                            }}
                            sx={{
                              textTransform: 'none',
                              borderRadius: 999,
                              fontWeight: 700
                            }}
                          >
                            {t('dashboard.navigate_to_settings', 'Go to settings')}
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  )}
                  primaryTypographyProps={{
                    sx: { fontWeight: 800, color: '#7f1d1d' }
                  }}
                />
              </ListItemButton>
              {index < dashboardErrorItems.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      </Popover>

      {outlookApiAuthSuccess && (
        <Alert
          severity='info'
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={handleOpenCaseSettings}
            >
              {t('dashboard.navigate_to_settings')}
            </IconButton>
          }
          >
          {t('dashboard.complete_outlook_settings')}
        </Alert>
      )}
      {dashboardErrorLogsDialog.open && (
        <Suspense fallback={null}>
          <ActionsLogsDialog
            open={dashboardErrorLogsDialog.open}
            onClose={handleCloseDashboardErrorLogs}
            title={dashboardErrorLogsDialog.title}
            caseid={dashboardErrorLogsDialog.caseid}
            timeevent_id={dashboardErrorLogsDialog.timeeventId}
          />
        </Suspense>
      )}

      {renderMainContent()}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={settingsDialogOpen}
        onClose={handleCloseCaseSettings}
        fullWidth
        maxWidth="xl"
        fullScreen={isMobile}
        aria-labelledby="case-settings-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.35)',
            width: '100%',
            m: { xs: 0, sm: '1.5rem auto' },
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle
          id="case-settings-dialog-title"
          sx={{
            background: 'linear-gradient(135deg, #5757b0, #8080d7)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, sm: 3 },
            py: 1.5
          }}
        >
          {t('dashboard.settings')}
          <CloseButton
            ariaLabel="close"
            onClick={handleCloseCaseSettings}
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              '&:hover': {
                color: '#fff',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
              }
            }}
          />
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 0, sm: 1 } }}>
          <Settings caseid={caseid} />
        </DialogContent>
      </Dialog>

      <Dialog
        open={eventListDialogOpen}
        onClose={handleCloseEventList}
        fullWidth
        maxWidth="xl"
        fullScreen={isMobile}
        aria-labelledby="events-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.35)',
            width: '100%',
            m: { xs: 0, sm: '1.5rem auto' },
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle
          id="events-dialog-title"
          sx={{
            background: 'linear-gradient(135deg, #5757b0, #8080d7)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, sm: 3 },
            py: 1.5
          }}
        >
          {t('dashboard.view_event_list', 'Events')}
          <CloseButton
            ariaLabel="close"
            onClick={handleCloseEventList}
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              '&:hover': {
                color: '#fff',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
              }
            }}
          />
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 0, sm: 1 } }}>
          <EventList caseid={caseid} folder={folder} onClose={handleCloseEventList} />
        </DialogContent>
      </Dialog>

      <AddToCollectionDialog
        open={addToCollectionDialogOpen}
        onClose={closeAddToCollectionDialog}
        onCancel={closeAddToCollectionDialog}
        onConfirm={handleAddToCollectionConfirm}
        collections={collections}
        caseTitle={title}
        loading={collectionsLoading}
      />

      {/* Timeline FAB + Drawer so they remain visible and usable when Datasources panel is shown */}
      {!isUploadOnlyAccess && ReactDOM.createPortal(
        <Box sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          zIndex: 1000
        }}>
          {showNewEventsBubble && (
            <Fade in timeout={240}>
              <Box
                sx={{
                  position: 'relative',
                  px: 1.5,
                  py: 0.6,
                  borderRadius: 999,
                  backgroundColor: '#fff',
                  color: '#d50000',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  boxShadow: '0 10px 24px rgba(255, 23, 68, 0.25)',
                  border: '1px solid rgba(255, 23, 68, 0.45)',
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    right: -5,
                    width: 10,
                    height: 10,
                    backgroundColor: '#fff',
                    borderRight: '1px solid rgba(255, 23, 68, 0.45)',
                    borderTop: '1px solid rgba(255, 23, 68, 0.45)',
                    transform: 'translateY(-50%) rotate(45deg)'
                  }
                }}
              >
                {t('dashboard.new_events', 'New Events')}
              </Box>
            </Fade>
          )}
          <Tooltip title={dashboardOutdated ? t("dashboard.new_data_available", "New updates available") : t('dashboard.view_event_list', 'Events')} arrow placement="left">
            <Fab
              color={dashboardOutdated ? "error" : "primary"}
              aria-label="events"
              onClick={() => {
                setDrawerOpen(true);
                setDashboardOutdated(false);
                sendMessage({ command: 'getcase', caseid });
                if (visibleSection !== "timeline") {
                  setVisibleSection("timeline");
                }
              }}
              sx={{
                backgroundColor: dashboardOutdated ? '#ff1744' : '#5757b0', // Red if outdated
                boxShadow: dashboardOutdated
                  ? '0 0 20px rgba(255, 23, 68, 0.6)'
                  : '0 18px 36px rgba(87, 87, 176, 0.35)',
                transition: 'all 0.3s cubic-bezier(0.68, -0.55, 0.27, 1.55)', // Bouncy transition
                width: { xs: 48, sm: 56 },
                height: { xs: 48, sm: 56 },
                // ... existing animations logic if needed, or we can simplify
                animation: dashboardOutdated
                  ? 'fabShake 0.5s ease-in-out infinite alternate'
                  : (isMobile ? 'none' : 'fabBounce 4.2s ease-in-out infinite'),

                '&:hover': {
                  backgroundColor: dashboardOutdated ? '#d50000' : '#7777d0',
                  transform: 'translateY(-3px) scale(1.05)',
                  boxShadow: dashboardOutdated
                    ? '0 0 30px rgba(255, 23, 68, 0.8)'
                    : '0 22px 44px rgba(87, 87, 176, 0.45)'
                },
                '@keyframes fabShake': {
                  '0%': { transform: 'rotate(-5deg)' },
                  '100%': { transform: 'rotate(5deg)' }
                },
                '@keyframes fabBounce': {
                  '0%, 100%': { transform: 'translateY(0)' },
                  '50%': { transform: 'translateY(-6px)' }
                },
                // ... keep other keyframes if they don't conflict
              }}
            >
              <StyledBadge
                overlap="circular"
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                variant="dot"
                invisible={!dashboardOutdated}
              >
                <EventIcon />
              </StyledBadge>
            </Fab>
          </Tooltip>
        </Box>,
        document.body
      )}

      {!isUploadOnlyAccess && ReactDOM.createPortal(
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{
            container: document.body,
            style: { position: 'fixed' }
          }}
          SlideProps={{
            direction: 'left'
          }}
          sx={{
            '& .MuiDrawer-paper': {
              right: 0,
              left: 'auto',
              width: { xs: '100%', sm: '90%', md: '70%', lg: '60%' },
              maxWidth: '1200px',
              height: '100vh',
              maxHeight: '100vh',
              background: 'linear-gradient(180deg, #ffffff 0%, #f5f5ff 55%, #f0f0ff 100%)',
              backgroundSize: '170% 170%',
              animation: 'timelineDrawerSlide 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both, timelineDrawerGlow 22s ease-in-out infinite alternate',
              boxShadow: '0 24px 60px rgba(15, 23, 42, 0.35)',
              borderTopLeftRadius: { xs: 0, md: 24 },
              borderBottomLeftRadius: { xs: 0, md: 24 },
              borderLeft: { xs: 'none', md: '1px solid rgba(87, 87, 176, 0.15)' },
              overflow: 'hidden',
              isolation: 'isolate',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(120deg, transparent 10%, rgba(87,87,176,0.06) 40%, transparent 70%)',
                opacity: 0.5,
                pointerEvents: 'none',
                animation: 'timelineDrawerSheen 12s ease-in-out infinite'
              }
            },
            '@keyframes timelineDrawerSlide': {
              '0%': { opacity: 0, transform: 'translateX(40px)' },
              '100%': { opacity: 1, transform: 'translateX(0)' }
            },
            '@keyframes timelineDrawerGlow': {
              '0%': { backgroundPosition: '0% 0%' },
              '100%': { backgroundPosition: '0% 90%' }
            },
            '@keyframes timelineDrawerSheen': {
              '0%, 60%': { opacity: 0, transform: 'translateX(0)' },
              '70%': { opacity: 0.5, transform: 'translateX(18%)' },
              '100%': { opacity: 0, transform: 'translateX(35%)' }
            }
          }}
        >
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #e0e0e0',
                position: 'sticky',
                top: 0,
                background: 'rgba(255,255,255,0.94)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 6px 18px rgba(87, 87, 176, 0.12)',
                zIndex: 1,
                animation: 'drawerHeaderDrop 0.4s ease both',
                '@keyframes drawerHeaderDrop': {
                  '0%': { opacity: 0, transform: 'translateY(-12px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' }
                }
              }}
            >
              <Typography variant="h6" sx={{ color: '#5757b0', fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {t('timeline.title', 'Timeline')}
              </Typography>
              <CloseButton
                onClick={() => setDrawerOpen(false)}
                sx={{
                  transition: 'transform 0.25s ease, color 0.25s ease',
                  '&:hover': {
                    transform: 'rotate(90deg) scale(1.05)',
                    color: '#d32f2f'
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 1, sm: 0 } }}>
              <Suspense fallback={<Loading />}>
                <TimelineEventsOptimized
                  caseid={caseid}
                  folder={folder}
                  initialEventId={eventToOpen}
                  onEventListOpen={isReadOnly ? null : handleOpenEventList}
                  onClose={() => { setDrawerOpen(false); setEventToOpen(null); }}
                />
              </Suspense>
            </Box>
          </Box>
        </Drawer>,
        document.body
      )}
    </>
  );
};


// Wrap the DashboardContent in the DashboardProvider to pass the context
const Dashboard = () => {
  const { caseid } = useParams();
  return (
    <DashboardProvider key={caseid}>
      <DashboardContent />
    </DashboardProvider>
  );
};

export default Dashboard;
